import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, TRAVEL_SUBCATEGORIES, ExpenseCategory, TravelSubcategory } from '../types';
import { 
  Upload as UploadIcon, 
  File, 
  X, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  FileText
} from 'lucide-react';

export default function Upload() {
  const navigate = useNavigate();
  const { addExpense } = useExpenses();
  
  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState<ExpenseCategory>('travel');
  const [subcategory, setSubcategory] = useState<TravelSubcategory>('uber');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
    setError('');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
    setError('');
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setIsUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await addExpense({
          category,
          subcategory: category === 'travel' ? subcategory : undefined,
          description,
          amount: amount ? parseFloat(amount) : undefined,
          date,
          file,
          filename: file.name,
        });
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      setSuccess(true);
      setTimeout(() => navigate('/expenses'), 2000);
    } catch (err) {
      setError('Failed to upload files. Please try again.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto text-center py-20 animate-fade-in">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {files.length === 1 ? 'Upload Successful!' : `${files.length} Files Uploaded!`}
        </h2>
        <p className="text-white/60">Redirecting to expenses...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Upload Files</h1>
        <p className="text-white/60 mt-1">Add receipts and documents from your device</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm animate-fade-in">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Dropzone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
            files.length > 0
              ? 'border-green-400/50 bg-green-500/10'
              : 'border-white/30 hover:border-white/50 hover:bg-white/5'
          }`}
        >
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            accept="image/*,.pdf,.doc,.docx"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            {files.length > 0 ? (
              <FileText className="w-12 h-12 text-green-400 mx-auto mb-4" />
            ) : (
              <UploadIcon className="w-12 h-12 text-white/40 mx-auto mb-4" />
            )}
            <p className="text-lg font-medium text-white">
              {files.length > 0 ? 'Tap to add more files' : 'Tap to select files'}
            </p>
            <p className="text-sm text-white/50 mt-1">
              or drag and drop • Images, PDFs, Documents
            </p>
          </label>
        </div>

        {/* Selected Files */}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-white/80">
                {files.length} file{files.length !== 1 ? 's' : ''} selected
              </p>
              <button
                type="button"
                onClick={() => setFiles([])}
                className="text-sm text-red-400 hover:text-red-300"
              >
                Clear all
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2 bg-white/5 rounded-xl p-3">
              {files.map((file, index) => (
                <div 
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between bg-white/10 rounded-lg p-3"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <File className="w-5 h-5 text-shuttle-sand flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-white text-sm truncate">{file.name}</p>
                      <p className="text-xs text-white/50">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4 text-white/40" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            Category
          </label>
          <div className="grid grid-cols-4 gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategory(cat.value)}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  category === cat.value
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-xl block">{cat.icon}</span>
                <span className="text-xs text-white/70 mt-1 block truncate">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Travel Subcategory */}
        {category === 'travel' && (
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Travel Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {TRAVEL_SUBCATEGORIES.map((sub) => (
                <button
                  key={sub.value}
                  type="button"
                  onClick={() => setSubcategory(sub.value)}
                  className={`p-2 rounded-xl border-2 text-center transition-all ${
                    subcategory === sub.value
                      ? 'border-shuttle-sand bg-shuttle-sand/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-lg block">{sub.icon}</span>
                  <span className="text-xs text-white/70 truncate block">{sub.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-white/80 mb-1">
            Description
          </label>
          <input
            id="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
          />
        </div>

        {/* Amount and Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-white/80 mb-1">
              Amount ($)
            </label>
            <input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-white/80 mb-1">
              Date
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Uploading...</span>
              <span className="font-medium text-shuttle-sand">{uploadProgress}%</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-shuttle-sand to-amber-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isUploading || files.length === 0}
          className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <UploadIcon className="w-5 h-5" />
              <span>
                {files.length === 0 
                  ? 'Select Files' 
                  : files.length === 1 
                  ? 'Upload 1 File' 
                  : `Upload ${files.length} Files`}
              </span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}






